import { useRef } from 'react';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { boxToPct, pctToStyle } from '../lib/coords';
import { fieldLabelText, fieldType } from '../lib/fieldTypes';

/** Pointer travel (px) below which a gesture counts as a click, not a drag. */
const DRAG_THRESHOLD = 3;

/**
 * A placed field on a builder page: pointer-drag to move, corner handle to
 * resize, click to select. All geometry is committed as percentages via
 * `onChange(id, pctPatch)` — no drag-and-drop library, just pointer capture.
 */
export default function FieldBox({ field, color, selected, onSelect, onChange, onDelete }) {
  const boxRef = useRef(null);
  const gesture = useRef(null);

  const startGesture = (event, mode) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect?.(field.id);
    const page = boxRef.current?.parentElement; // the overlay layer covering the page
    if (!page) return;
    const pageRect = page.getBoundingClientRect();
    gesture.current = {
      mode,
      pageRect,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      posTop: Number(field.posTop),
      posLeft: Number(field.posLeft),
      posWidth: Number(field.posWidth),
      posHeight: Number(field.posHeight),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveGesture = (event) => {
    const g = gesture.current;
    if (!g) return;
    const dx = event.clientX - g.startX;
    const dy = event.clientY - g.startY;
    // A sub-threshold gesture is a click (select only) — never nudge geometry.
    if (!g.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      g.moved = true;
    }
    const { width: pw, height: ph } = g.pageRect;

    if (g.mode === 'move') {
      const px = (g.posLeft / 100) * pw + dx;
      const py = (g.posTop / 100) * ph + dy;
      const pct = boxToPct(
        { x: px, y: py, width: (g.posWidth / 100) * pw, height: (g.posHeight / 100) * ph },
        pw,
        ph,
      );
      onChange(field.id, { posLeft: pct.posLeft, posTop: pct.posTop });
    } else {
      const width = Math.max(16, (g.posWidth / 100) * pw + dx);
      const height = Math.max(12, (g.posHeight / 100) * ph + dy);
      const pct = boxToPct(
        { x: (g.posLeft / 100) * pw, y: (g.posTop / 100) * ph, width, height },
        pw,
        ph,
      );
      onChange(field.id, { posWidth: pct.posWidth, posHeight: pct.posHeight });
    }
  };

  const endGesture = (event) => {
    if (gesture.current) event.currentTarget.releasePointerCapture?.(event.pointerId);
    gesture.current = null;
  };

  const meta = fieldType(field.kind);
  const Icon = meta?.icon;

  return (
    <div
      ref={boxRef}
      role="button"
      tabIndex={0}
      onPointerDown={(e) => startGesture(e, 'move')}
      onPointerMove={moveGesture}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
      // Keep the click inside the box: the pages container deselects on click,
      // and an armed palette kind would otherwise drop a new field here.
      onClick={(e) => e.stopPropagation()}
      className={clsx(
        'group absolute flex cursor-grab touch-none items-center gap-1 overflow-hidden rounded-sm border px-1 text-[11px] font-medium active:cursor-grabbing',
        selected ? 'z-10 ring-2 ring-offset-1' : 'hover:z-10',
      )}
      style={{
        ...pctToStyle(field),
        borderColor: color,
        backgroundColor: `${color}1f`,
        color,
        '--tw-ring-color': color,
      }}
    >
      {Icon && <Icon className="h-3 w-3 shrink-0" />}
      {/* Always captioned while authoring; `showLabel` only governs the signer view and sealed PDF. */}
      <span className="truncate">{fieldLabelText(field)}</span>
      {field.required && <span aria-hidden>*</span>}

      {selected && (
        <button
          type="button"
          aria-label="Remove field"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(field.id);
          }}
          className="absolute -right-2.5 -top-2.5 rounded-full bg-white p-0.5 text-red-500 shadow ring-1 ring-[#e2e8f0] hover:bg-[#fee2e2]"
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {/* Resize handle: always live, revealed on hover so any field is resizable. */}
      <span
        role="presentation"
        onPointerDown={(e) => startGesture(e, 'resize')}
        onPointerMove={moveGesture}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        className={clsx(
          'absolute bottom-0 right-0 h-3 w-3 cursor-nwse-resize touch-none transition-opacity',
          selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
        style={{ background: `linear-gradient(135deg, transparent 50%, ${color} 50%)` }}
      />
    </div>
  );
}
